import {
  TAIWAN_ADDRESS_MANUAL_SOURCE_MODE,
  createTaiwanAddressAnswerWithSource,
  formatTaiwanAddressAnswer,
  getTaiwanAddressDistricts,
  getTaiwanAddressPostalCode,
  getTaiwanAddressSourceMode,
  getTaiwanAddressSourceOptions,
  normalizeTaiwanAddressAnswer,
  taiwanAddressCities,
} from '@/lib/taiwan-address'
import type { AnswerInputProps } from '../QuestionRenderer'

export function TaiwanAddressInput({
  question,
  value,
  answers,
  onChange,
  onSubmit,
}: AnswerInputProps) {
  const address = normalizeTaiwanAddressAnswer(value)
  const districts = getTaiwanAddressDistricts(address.city)
  const fieldId = `taiwan-address-${question.id}`
  const sourceOptions = getTaiwanAddressSourceOptions(question)
  const selectedSourceMode = getTaiwanAddressSourceMode(question, value)
  const selectedSourceOption = sourceOptions.find((option) => option.value === selectedSourceMode)
  const selectedSourceAddress = selectedSourceOption?.sourceQuestionId
    ? answers?.get(selectedSourceOption.sourceQuestionId)
    : undefined
  const selectedSourceSummary = selectedSourceOption?.sourceQuestionId
    ? formatTaiwanAddressAnswer(selectedSourceAddress)
    : ''
  const showManualFields =
    sourceOptions.length === 0 ||
    !selectedSourceOption?.sourceQuestionId ||
    selectedSourceMode === TAIWAN_ADDRESS_MANUAL_SOURCE_MODE

  function emit(nextValue: Partial<typeof address>) {
    const city = nextValue.city !== undefined ? nextValue.city : address.city
    const cityChanged = nextValue.city !== undefined && nextValue.city !== address.city
    const district = cityChanged
      ? null
      : nextValue.district !== undefined
        ? nextValue.district
        : address.district
    const districtChanged =
      nextValue.district !== undefined && nextValue.district !== address.district
    const postalCode =
      nextValue.postalCode !== undefined
        ? nextValue.postalCode || null
        : district
          ? cityChanged || districtChanged
            ? getTaiwanAddressPostalCode(city, district)
            : address.postalCode
          : null
    const detail = nextValue.detail !== undefined ? nextValue.detail : address.detail

    const nextAddress = {
      city,
      district,
      postalCode,
      detail,
    } satisfies Record<string, string | null>

    onChange(
      sourceOptions.length > 0
        ? {
            ...nextAddress,
            addressSourceMode: TAIWAN_ADDRESS_MANUAL_SOURCE_MODE,
            addressSourceQuestionId: null,
            addressSourceLabel: null,
          }
        : nextAddress,
    )
  }

  function selectSourceMode(mode: string) {
    const option = sourceOptions.find((item) => item.value === mode)
    if (!option) return

    if (!option.sourceQuestionId || mode === TAIWAN_ADDRESS_MANUAL_SOURCE_MODE) {
      onChange(
        createTaiwanAddressAnswerWithSource(value, {
          mode: TAIWAN_ADDRESS_MANUAL_SOURCE_MODE,
        }),
      )
      return
    }

    onChange(
      createTaiwanAddressAnswerWithSource(answers?.get(option.sourceQuestionId), {
        mode: option.value,
        sourceQuestionId: option.sourceQuestionId,
        sourceLabel: option.label,
      }),
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {sourceOptions.length > 0 ? (
        <div className="grid gap-2">
          {sourceOptions.map((option) => {
            const optionId = `${fieldId}-source-${option.value}`
            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-indigo-200 hover:bg-indigo-50/40"
              >
                <input
                  id={optionId}
                  type="radio"
                  name={`${fieldId}-source`}
                  value={option.value}
                  checked={selectedSourceMode === option.value}
                  onChange={() => selectSourceMode(option.value)}
                  className="size-4 accent-[var(--form-accent,#6366F1)]"
                />
                <span>{option.label}</span>
              </label>
            )
          })}
          {!showManualFields ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-600">
              {selectedSourceSummary ||
                `請先完成${selectedSourceOption?.sourceQuestionTitle ?? '來源'}。`}
            </div>
          ) : null}
        </div>
      ) : null}

      {showManualFields ? (
        <>
          <div className="grid gap-3 sm:grid-cols-[minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(8rem,0.8fr)]">
            <div className="flex min-w-0 flex-col gap-2">
              <label htmlFor={`${fieldId}-city`} className="text-sm font-medium text-slate-700">
                縣市
              </label>
              <select
                id={`${fieldId}-city`}
                value={address.city ?? ''}
                onChange={(event) => emit({ city: event.target.value || null })}
                className="h-12 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[var(--form-accent,#6366F1)] focus:ring-2 focus:ring-indigo-100"
              >
                <option value="">請選擇縣市</option>
                {taiwanAddressCities.map((city) => (
                  <option key={city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <label htmlFor={`${fieldId}-district`} className="text-sm font-medium text-slate-700">
                區域
              </label>
              <select
                id={`${fieldId}-district`}
                value={address.district ?? ''}
                disabled={!address.city}
                onChange={(event) => emit({ district: event.target.value || null })}
                className="h-12 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none disabled:bg-slate-100 disabled:text-slate-400 focus:border-[var(--form-accent,#6366F1)] focus:ring-2 focus:ring-indigo-100"
              >
                <option value="">{address.city ? '請選擇區域' : '請先選縣市'}</option>
                {districts.map((district) => (
                  <option key={district.name} value={district.name}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <label
                htmlFor={`${fieldId}-postal-code`}
                className="text-sm font-medium text-slate-700"
              >
                5 碼郵遞區號
              </label>
              <input
                id={`${fieldId}-postal-code`}
                value={address.postalCode ?? ''}
                onChange={(event) => emit({ postalCode: event.target.value })}
                className="h-12 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition-colors focus:border-[var(--form-accent,#6366F1)] focus:ring-2 focus:ring-indigo-100"
                placeholder="選區域後顯示"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor={`${fieldId}-detail`} className="text-sm font-medium text-slate-700">
              詳細地址
            </label>
            <input
              id={`${fieldId}-detail`}
              value={address.detail}
              placeholder={String(
                question.settings.detailPlaceholder ?? '請輸入路名、段、巷弄、門牌、樓層',
              )}
              onChange={(event) => emit({ detail: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onSubmit()
              }}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-3 text-base outline-none transition-colors focus:border-[var(--form-accent,#6366F1)] focus:ring-2 focus:ring-indigo-100"
              style={{
                color: 'var(--form-answer,#334155)',
                fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                lineHeight: 'var(--form-line-height,1.55)',
              }}
            />
          </div>
        </>
      ) : null}

      {question.settings.errorMessage ? (
        <div className="text-sm text-rose-500">{question.settings.errorMessage}</div>
      ) : null}
    </div>
  )
}
